<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">

    <!-- Sidebar -->
    <AcdSidebar :isOpen="isSidebarOpen" />

    <!-- Main content -->
    <main class="flex-1 transition-all duration-300" :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}">
      
      <!-- Topbar -->
      <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
        <AcdTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="p-6 mt-16">

        <!-- Search + Add Holiday Type -->
        <div class="flex justify-between mb-6">
          <input
            v-model="search"
            type="text"
            placeholder="Search holiday type..."
            class="border rounded-lg px-4 py-2 w-72 dark:bg-gray-700 dark:text-white"
          />
          <button
            @click="openModal"
            class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f]"
          >
            + Add Holiday Type
          </button>
        </div>

        <!-- Holiday Types Grid -->
        <div v-if="filteredHolidayTypes.length" class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="type in filteredHolidayTypes" :key="type.id" class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-lg">
            
            <div class="flex items-center justify-between mb-4">
              <div>
                <h3 class="font-semibold text-gray-800 dark:text-white">{{ type.name }}</h3>
                <p class="text-sm text-gray-500">{{ type.code }}</p>
              </div>
              <div class="flex space-x-2">
                <button @click="editType(type)" class="text-blue-600 hover:underline text-sm">Edit</button>
                <button @click="deleteType(type.id)" class="text-red-500 hover:underline text-sm">Delete</button>
              </div>
            </div>

            <p class="text-gray-500 text-sm mb-2">Default Duration: {{ type.default_duration || '-' }} days</p>
            <p class="text-gray-500 text-sm">{{ type.description || '-' }}</p>
          </div>
        </div>

        <div v-else class="text-center text-gray-400 mt-20">No holiday types created yet</div>

        <!-- Modal -->
        <Transition name="modal">
          <div v-if="showModal" class="fixed inset-0 bg-black/40 flex items-center justify-center">
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px]">
              <h3 class="text-lg font-semibold text-[#035a76] mb-6">
                {{ editing ? "Edit Holiday Type" : "Create Holiday Type" }}
              </h3>
              <form @submit.prevent="saveType" class="space-y-4">

                <input v-model="form.name" type="text" placeholder="Holiday Type Name (e.g., Midterm Break)" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.code" type="text" placeholder="Code (e.g., MIDTERM)" required class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <input v-model="form.default_duration" type="number" placeholder="Default Duration (days)" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white" />
                <textarea v-model="form.description" placeholder="Description" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"></textarea>

                <div class="flex justify-end space-x-3">
                  <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 rounded">Cancel</button>
                  <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">Save</button>
                </div>
              </form>
            </div>
          </div>
        </Transition>

      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import api from "@/services/api";
import AcdSidebar from "@/components/acd/AcdSidebar.vue";
import AcdTopbar from "@/components/acd/AcdTopbar.vue";

const currentPageTitle = ref("Holiday Types");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);

const showModal = ref(false);
const editing = ref(false);
const search = ref("");

const holidayTypes = ref([]);

// const holidayTypes = ref([
//   { id: 1, name: "Midterm Break", code: "MIDTERM", default_duration: 5, description: "Midterm break for students" },
//   { id: 2, name: "Christmas Break", code: "XMAS", default_duration: 7, description: "Christmas break" }
// ]);

const form = ref({ id: null, name: "", code: "", default_duration: null, description: "" });

const filteredHolidayTypes = computed(() =>
  holidayTypes.value.filter(t => t.name.toLowerCase().includes(search.value.toLowerCase()))
);

const openModal = () => {
  editing.value = false;
  form.value = { id: null, name: "", code: "", default_duration: null, description: "" };
  showModal.value = true;
};

const editType = (type) => {
  editing.value = true;
  form.value = { ...type };
  showModal.value = true;
};

const fetchHolidayTypes = async () => {
    try {
        const response = await api.get("HolidayTypes.php");
        holidayTypes.value = response.data;
    } catch (error) {
        console.error("Error fetching holiday types:", error);
    }
}


const saveType = async () => {
    try{
        let response; 
           
        if(editing.value){
            response = await api.put("HolidayTypes.php", form.value)
            const index = holidayTypes.value.findIndex(h => h.id === form.value.id)
            if (index !== -1) {
                holidayTypes.value[index] = { ...form.value };
            }
        }else{
            response = await api.post("HolidayTypes.php", form.value)
            holidayTypes.value.push({
                id:response.data.id,
                name:response.data.name,
                code:response.data.code,
                default_duration:response.data.default_duration,
                description:response.data.description
            });
        }
        closeModal();

  } catch (error) {
    console.error("Error saving Holiday Type :", error);
  }
};


// const saveType = () => {
//   if (!form.value.name || !form.value.code) return alert("Name and Code are required.");

//   if (editing.value) {
//     const index = holidayTypes.value.findIndex(t => t.id === form.value.id);
//     if (index !== -1) holidayTypes.value[index] = { ...form.value };
//   } else {
//     holidayTypes.value.push({ id: Date.now(), ...form.value });
//   }

//   closeModal();
// };

const deleteType = (id) => {
  if (confirm("Are you sure you want to delete this holiday type?")) {
    holidayTypes.value = holidayTypes.value.filter(t => t.id !== id);
  }
};

const closeModal = () => showModal.value = false;

const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value;
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
};

onMounted(() => {
  fetchHolidayTypes();
});
</script>

<style>
.modal-enter-active,
.modal-leave-active { transition: all .25s ease; }
.modal-enter-from { opacity: 0; transform: scale(.9); }
.modal-leave-to { opacity: 0; transform: scale(.9); }
</style>